// Pagina de Caixa — orquestracao (redesenhada na v1.5.784: numeros grandes,
// acoes grandes, fechamento em dois passos com conferencia por forma).
// A logica de servidor vive em hooks/, as chamadas HTTP em api/, as contas
// da tela em lib/ e cada bloco de UI em components/.
// 25/10/2026 (dono): as ações sobem para logo abaixo do título, e a tela vira
// duas abas — "Caixa de agora" e "Caixas anteriores", cujo fechamento abre num
// painel lateral (DetalheFechamento) sem precisar imprimir.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, Wallet } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { CarregandoCards, CarregandoLista } from "../../components/carregando";
import { Skeleton } from "../../components/ui/skeleton";
import { imprimirCupomCaixa } from "../../utils/cupomCaixa";

import { caixaApi } from "./api/caixaApi";
import { useCaixaData } from "./hooks/useCaixaData";
import { useMovimentoDialog } from "./hooks/useMovimentoDialog";
import { useFiltrosHistorico } from "./hooks/useFiltrosHistorico";
import { inteiro, paraCupom } from "./lib/caixa";

import AcoesCaixa from "./components/AcoesCaixa";
import CaixaFechado from "./components/CaixaFechado";
import DetalheFechamento from "./components/DetalheFechamento";
import HistoricoCaixas from "./components/HistoricoCaixas";
import MovimentoDialog from "./components/MovimentoDialog";
import Movimentacoes from "./components/Movimentacoes";
import PainelAberto from "./components/PainelAberto";
import VendasPorForma from "./components/VendasPorForma";
import FechamentoCaixaDialog from "./fechamento/FechamentoCaixaDialog";

function Status({ aberto }) {
  return aberto
    ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />Aberto</span>
    : <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground"><span className="w-2 h-2 rounded-full bg-muted-foreground" />Fechado</span>;
}

const TODAS = { abrir: true, fechar: true, movimentar: true };

export default function Caixa({ caixaAberto, setCaixaAberto, onAbrirCaixa, permissoes = TODAS }) {
  const navigate = useNavigate();
  const { movimentos, resumo, historico, esperando, refresh } = useCaixaData(caixaAberto, setCaixaAberto);
  const sangria = useMovimentoDialog();
  const suprimento = useMovimentoDialog();
  const [fecharOpen, setFecharOpen] = useState(false);
  const filtros = useFiltrosHistorico(historico);
  const [aba, setAba] = useState("agora");
  const [vendo, setVendo] = useState(null);

  const movimentar = (dialog, chamada, sucesso) => async () => {
    if (dialog.valor <= 0) return toast.error("Informe um valor maior que zero");
    try {
      await chamada(caixaAberto.id, dialog.valor, dialog.observacao);
      toast.success(sucesso);
      dialog.close();
      refresh();
    } catch (e) {
      toast.error(e.message || "Erro ao registrar");
    }
  };

  const reimprimir = async (caixa) => {
    try {
      const [movs, res] = await Promise.all([caixaApi.listarMovimentos(caixa.id), caixaApi.obterResumo(caixa.id)]);
      await imprimirCupomCaixa(paraCupom(caixa), movs, res);
      toast.success("Cupom enviado para impressão");
    } catch (e) {
      toast.error("Erro ao reimprimir");
    }
  };

  // Prefixo /admin é obrigatório — App.js só monta o Dashboard em /admin/*.
  const verPedidos = (caixaId) => navigate(`/admin/vendas/pedidos?caixa_id=${caixaId}`);
  const fechados = (historico || []).filter((h) => h.status === "fechado");
  const ultimoFechado = fechados[0] || null;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-e2e="tela-caixa">
      <div className="flex items-center gap-3 flex-wrap">
        <Wallet className="w-7 h-7 text-primary" />
        <h1 className="text-2xl sm:text-3xl font-bold">Caixa</h1>
        {/* Enquanto não se sabe se há caixa aberto, nem "Aberto" nem "Fechado" */}
        {esperando && !caixaAberto ? <Skeleton className="h-6 w-20 rounded-full" /> : <Status aberto={!!caixaAberto} />}
        {/* Sem botão Atualizar (o do cabeçalho já recarrega) nem Recalcular vendas:
            o resumo e o fechamento já lançam sozinhos a venda que ficou fora (dono, 25/10/2026) */}
      </div>

      {/* Tirar, colocar e fechar logo abaixo do título (dono, 25/10/2026) */}
      {caixaAberto && (
        <AcoesCaixa onSangria={sangria.open} onSuprimento={suprimento.open} onFechar={() => setFecharOpen(true)} permissoes={permissoes} />
      )}

      <Tabs value={aba} onValueChange={setAba}>
        <TabsList className="h-11 w-full sm:w-auto">
          <TabsTrigger value="agora" className="flex-1 sm:flex-none px-5 h-9" data-e2e="aba-caixa-agora">Caixa de agora</TabsTrigger>
          <TabsTrigger value="anteriores" className="flex-1 sm:flex-none px-5 h-9" data-e2e="aba-caixas-anteriores">
            Caixas anteriores{filtros.totalGeral ? <span className="ml-1.5 text-xs text-muted-foreground">({inteiro(filtros.totalGeral)})</span> : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="agora" className="mt-4 space-y-4">
          {esperando ? (
            // Carregamento com cards (25/10/2026): os três números, as vendas por
            // forma e as movimentações piscando até o resumo do caixa chegar.
            <>
              <CarregandoCards quantos={3} className="grid gap-3 sm:grid-cols-3" altura="h-[121px]" />
              <div className="grid gap-4 lg:grid-cols-2">
                <CarregandoLista linhas={3} avatar={false} colunas={1} />
                <CarregandoLista linhas={3} colunas={1} />
              </div>
            </>
          ) : caixaAberto ? (
            <>
              <PainelAberto caixa={caixaAberto} resumo={resumo} />
              <div className="grid gap-4 lg:grid-cols-2">
                <VendasPorForma resumo={resumo} />
                <Movimentacoes movimentos={movimentos} onVerPedidos={() => verPedidos(caixaAberto.id)} />
              </div>
            </>
          ) : (
            <CaixaFechado ultimo={ultimoFechado} onAbrir={onAbrirCaixa} podeAbrir={permissoes.abrir} onVerUltimo={setVendo} />
          )}
        </TabsContent>

        <TabsContent value="anteriores" className="mt-4">
          <HistoricoCaixas filtros={filtros} onAbrir={setVendo} />
        </TabsContent>
      </Tabs>

      <DetalheFechamento caixa={vendo} onClose={() => setVendo(null)} onReimprimir={reimprimir} onVerPedidos={verPedidos} />

      <MovimentoDialog
        open={sangria.isOpen} onOpenChange={sangria.setIsOpen}
        title="Tirar dinheiro (sangria)" icon={<ArrowUpFromLine className="w-5 h-5 text-red-500" />}
        buttonLabel="Confirmar sangria" buttonClass="bg-red-500 hover:bg-red-600 text-white"
        placeholderObs="Para onde vai? Ex.: cofre, pagar fornecedor"
        valor={sangria.valor} setValor={sangria.setValor} observacao={sangria.observacao} setObservacao={sangria.setObservacao}
        onConfirm={movimentar(sangria, caixaApi.sangria, "Sangria registrada")}
      />
      <MovimentoDialog
        open={suprimento.isOpen} onOpenChange={suprimento.setIsOpen}
        title="Colocar dinheiro (suprimento)" icon={<ArrowDownToLine className="w-5 h-5 text-green-500" />}
        buttonLabel="Confirmar suprimento" buttonClass="bg-green-600 hover:bg-green-700 text-white"
        placeholderObs="De onde veio? Ex.: troco extra do cofre"
        valor={suprimento.valor} setValor={suprimento.setValor} observacao={suprimento.observacao} setObservacao={suprimento.setObservacao}
        onConfirm={movimentar(suprimento, caixaApi.suprimento, "Suprimento registrado")}
      />
      <FechamentoCaixaDialog
        open={fecharOpen} onOpenChange={setFecharOpen} caixaAberto={caixaAberto}
        onFechado={() => { setCaixaAberto(null); refresh(); }}
      />
    </div>
  );
}
